import { Video, MessageSquare, ClipboardList, Flag, Repeat, Ban } from 'lucide-react';
import { type AppointmentStatusType } from '@/components/atoms';
import { StatusDot } from '@/components/ui/status-dot';
import { AppointmentTileMeta } from '@/components/blocks/appointment-tile-meta';
import { cn } from '@/lib/utils';

/**
 * Diagonal-hatch background for Block Availability tiles — the React idiom for
 * Flutter's `ic_strips.png` over a muted grey (`calendar_event_card_widget.dart
 * :160-166`). Signals "unavailable / not a patient appointment" at a glance.
 */
export const BLOCK_STRIPES =
  'bg-[repeating-linear-gradient(45deg,var(--color-grey-200)_0,var(--color-grey-200)_5px,var(--color-grey-100)_5px,var(--color-grey-100)_10px)]';

/**
 * AppointmentTile — Component (Card-level)
 * Calendar appointment tile displayed in the week/day grid.
 * Color-coded left border by status. Keyboard focusable.
 *
 * FHIR mapping (documentation — Stream 1 uses DTOs):
 *   patient → Appointment.participant[patient].display
 *   status → Appointment.status
 *   timeRange → Appointment.start / Appointment.end
 *   note → Appointment.comment
 */

const STATUS_EDGE_COLORS: Record<AppointmentStatusType, string> = {
  arrived: 'bg-brand-primary',
  confirmed: 'bg-status-success',
  booked: 'bg-blue-200',
  noShow: 'bg-status-error',
  cancelled: 'bg-grey-400',
  waitlisted: 'bg-status-warning',
  checkedIn: 'bg-blue-300',
  fulfilled: 'bg-status-success',
};

interface AppointmentTileProps {
  patientName: string;
  status: AppointmentStatusType;
  timeRange: string;
  /** @deprecated unused by the tile body — kept as optional for caller compatibility. */
  durationMinutes?: number;
  note?: string;
  hasVideo?: boolean;
  hasChat?: boolean;
  hasNote?: boolean;
  hasFlag?: boolean;
  /**
   * True when the appointment is part of a recurring series. Renders a
   * loop indicator in the icon strip (Flutter parity:
   * `appointment_status.dart` shows the same glyph for series members).
   * Tooltip hint: hover shows "Recurring".
   */
  isRecurring?: boolean;
  /**
   * True when this is a Block Availability entry, not a patient appointment.
   * Renders the striped grey block variant (reason/`note` + time + recurring
   * icon) instead of the patient/status layout. Flutter parity:
   * `calendar_event_card_widget.dart:157-216`.
   */
  isBlock?: boolean;
  /**
   * Patient importance + location, for the tile meta row (PROD-990).
   * All optional — when omitted, the location icon falls back to the
   * office glyph (matching the tile's prior always-office indicator) and
   * the importance badge is hidden.
   */
  patientImportance?: string;
  patientImportanceDisplay?: string;
  /** office / home / virtual — selects the location glyph. */
  profileLocation?: string;
  /** Human location name — the location-icon tooltip. */
  locationName?: string;
  /** Profile colour (CSS) tinting the location glyph. */
  profileColor?: string;
  /** Height in px — computed by parent grid from duration */
  height: number;
  /** Top offset in px — computed by parent grid from start time */
  top: number;
  /**
   * Horizontal placement for overlapping appointments (PROD-982). When both
   * are set, the tile is positioned at `left: leftPct%` with `width: widthPct%`
   * (a small gutter is subtracted) so concurrent appointments sit side-by-side
   * instead of stacking. When omitted, the tile spans the column (full width).
   */
  leftPct?: number;
  widthPct?: number;
  onClick?: () => void;
  className?: string;
  /** Over (its end has passed): drawn muted, almost no saturation; colour is for the day ahead (Antonin, 12 Oct). */
  isPast?: boolean;
}

/** A past tile: grey-100 surface, grey-500 edge, secondary text (no opacity: text keeps its contrast). */
const PAST_TILE = 'bg-[var(--color-grey-100)] text-text-secondary';

import { locationTileBg, locationEdgeColor } from '@/scheduling/lib/location-colors';

export function AppointmentTile({
  patientName,
  status,
  timeRange,
  note,
  hasVideo,
  hasChat,
  hasNote,
  hasFlag,
  isRecurring,
  isBlock,
  patientImportance,
  patientImportanceDisplay,
  profileLocation,
  locationName,
  profileColor,
  height,
  top,
  leftPct,
  widthPct,
  onClick,
  className,
  isPast = false,
}: AppointmentTileProps) {
  const showNote = note && height > 40;

  // Overlap geometry (PROD-982). `leftPct != null` (NOT a truthy check) so a
  // legit left-edge tile at leftPct === 0 still positions at `left: 0%`
  // instead of silently falling back to full width (Samer review §4/§5).
  // Gutter is a small FIXED 2px on the right via the width — capped, so it
  // never eats a disproportionate share of a narrow tile at high column
  // counts (Samer review §2). Spans the column via Tailwind when omitted.
  const isOverlap = leftPct != null && widthPct != null;
  const posClass = isOverlap ? 'absolute' : 'absolute left-1 right-1';
  const overlapStyle = isOverlap
    ? { left: `${leftPct}%`, width: `calc(${widthPct}% - 2px)` }
    : null;

  if (isBlock) {
    const label = note?.trim() || 'Blocked';
    return (
      <button
        onClick={onClick}
        className={cn(
          posClass,
          'rounded-md border border-border-default overflow-hidden cursor-pointer hover:shadow-md transition-shadow text-left',
          BLOCK_STRIPES,
          className,
        )}
        style={{ top: `${top}px`, height: `${height}px`, ...overlapStyle }}
        aria-label={`Blocked${label !== 'Blocked' ? `: ${label}` : ''}, ${timeRange}`}
      >
        <div className="px-2 py-1 h-full overflow-hidden leading-snug">
          <div className="flex items-center gap-1">
            {isRecurring ? (
              <Repeat
                className="h-2 w-2 text-text-muted flex-shrink-0"
                aria-label="Recurring block"
              />
            ) : (
              <Ban className="h-2 w-2 text-text-muted flex-shrink-0" />
            )}
            <span className="text-xs font-medium text-text-secondary truncate leading-tight">
              {label}
            </span>
          </div>
          <span className="text-xs text-text-muted leading-none">{timeRange}</span>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        posClass,
        'rounded-md border overflow-hidden cursor-pointer hover:shadow-md transition-shadow text-left',
        isPast ? PAST_TILE : '',
        className
      )}
      style={{
        top: `${top + 3}px`,
        height: `${Math.max(height - 9, 20)}px`,
        ...overlapStyle,
        ...(!isPast ? { backgroundColor: locationTileBg(profileLocation) } : {}),
      }}
      aria-label={`${patientName}, ${status}, ${timeRange}`}
      data-past={isPast ? '' : undefined}
    >
      {/* The status edge is the card's whole left shoulder, a 4 px colour bar clipped by the card's corners, not a left border bent by them (Antonin, 13 Oct). */}
      <span aria-hidden data-event-edge className={cn('absolute inset-y-0 left-0 w-1', !isPast && STATUS_EDGE_COLORS[status])} style={isPast ? { backgroundColor: locationEdgeColor(profileLocation, profileColor), opacity: 0.4 } : undefined} />
      <div className="pl-3 pr-2 py-1 h-full overflow-hidden leading-snug">
        <div className="flex items-center justify-between gap-1">
          <span className={cn('text-xs font-medium truncate leading-tight', isPast ? 'text-text-secondary' : 'text-text-primary')}>
            {patientName}
          </span>
          {/* Status as a shape plus colour with an instant tooltip, never a filled badge (one rule for every app). */}
          <StatusDot status={status} />
        </div>
        <div className="flex items-center gap-1 mt-1">
          {/* Importance badge + location icon (PROD-990) — replaces the
              previous hardcoded office glyph with the real location. */}
          <AppointmentTileMeta
            location={profileLocation}
            locationName={locationName}
            profileColor={profileColor}
            importance={patientImportance}
            importanceDisplay={patientImportanceDisplay}
          />
          <span className="text-xs text-text-muted leading-none">{timeRange}</span>
          {hasVideo && <Video className="h-2 w-2 text-text-muted" />}
          {hasChat && <MessageSquare className="h-2 w-2 text-text-muted" />}
          {hasNote && <ClipboardList className="h-2 w-2 text-text-muted" />}
          {/* Order (Andrew 2026-06-10): recurring before flag. */}
          {isRecurring && (
            <Repeat className="h-2 w-2 text-brand-primary" aria-label="Recurring" />
          )}
          {/* Flag tint = brand-primary (Flutter `enabledBtnBGColor` #00B8E3
              teal → Signal Blue/500 anchor). status-warning orange was a
              rule-2 decoration misuse. */}
          {hasFlag && <Flag className="h-2 w-2 text-brand-primary" />}
        </div>
        {showNote && (
          <p className="text-xs text-brand-primary mt-1 line-clamp-2 leading-tight">{note}</p>
        )}
      </div>
    </button>
  );
}
